import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import TextArea from 'components/final-form/TextArea';
import TimeInput from 'components/final-form/TimeInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import QuestionSetView from 'components/patient-entry/QuestionSetView';
import { DataEntry, DataEntryValue, Label } from 'components/patient-entry/styled';
import {
  convertValueToList,
  extractValueFromList,
  generateRadioButtonOptions
} from 'components/patient-entry/upsert/UpsertAnamnesis';
import { FormApi } from 'final-form';
import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, List, ListItem, Loader } from 'semantic-ui-react';
import axios from 'services/http';
import {
  getInterventionBookingAnswersData,
  setControlledByBookingStatus,
  setQuestionTestResult,
  setResultRejectedBookingStatus,
  verifyQuestionTestResult
} from 'services/interventionBookingServices';
import styled from 'styled-components';
import { InterventionBookingStatus, InterventionExternalCode } from 'ts-types/api.enums';
import {
  InterventionTestBookingSearchResult,
  QuestionAnswerDto,
  QuestionDto,
  QuestionSetDto,
  QuestionTestResultRequest
} from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { required } from 'utils/validatorUtils';

const StyledDataEntry = styled(DataEntry)`

  margin-top: 1em;

  & .label {
    min-width: 32px;
  }
`;

const StyledTextArea = styled(TextArea)`
  margin-top: 1em !important;
`;

const StyledForm = styled.form`
  padding: 0.75rem;
  width: 100%;

  .patient-data {
    font-size: 1.1rem;
    font-weight: 500;

    .barcode {
      color: #7a97ff;
    }
  }

  .field-label {
    margin-top: 0.4rem;
    margin-bottom: 0.7rem;

    .inline-span {
      display: inline-block;
      margin-right: 1rem;
    }
  }

  .field-label:not(:first-child) {
  }

  .multi-field-row {
    display: flex;
    flex-direction: row;

    & > div {
      margin-right: 1.5rem;
    }

    .date-input {
      width: 8rem;
    }

    .time-input .ui.mini.input {
      width: 6rem;
    }
  }

  .radio-group .ui.radio.checkbox {
    margin-right: 1.5rem;
  }

  & .ui.grid {
    padding-left: 0 !important;
  }

  .field-error {
    color: darkred;
    font-size: 1rem;
  }

  .loader-box {
    position: relative;
    width: 300px;
    height: 90px;
  }

  .test-result-warning {
    color: #b58105;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .test-result-error {
    color: darkred;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .unanswered-questions-error {
    color: darkred;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .previous-test-results {
    width: 100%;
    border-top: 1px solid #c5c5c5;
    padding-top: 0.5rem;

    .no-results-message {
      font-style: italic;
      color: grey
    }
  }

  .actions-row {
    margin-top: 2rem;

    button {
      margin-right: 1rem;
    }
  }
`;

interface InterventionQuestionTestBookingSearchResult extends InterventionTestBookingSearchResult {
  questionSetId: number,
  questionAnswerPairs: QuestionAnswerDto[]
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, PatientCentralRefDataConsumerRenderProps {
  interventionBooking: InterventionTestBookingSearchResult,
  onClose: () => void,
  onCloseAndUpdate: (bookingId: number) => void
}

interface State {
  requiredDataLoaded: boolean,
  testIntervention?: InterventionExternalCode,
  questionSets: Array<QuestionSetDto>;
  questionSetMap: { [key: number]: QuestionSetDto };
  selectedQuestionSet?: QuestionSetDto;
  currentQuestion: number;
  modifySampleDateTime: boolean,
  testResultInProgress: boolean,
  testResultSaveError?: string,
  testResultSaveWarning?: string,
  unansweredQuestions?: string,
  errorMessage?: string
}

class SetQuestionTestResultModal extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<InterventionQuestionTestBookingSearchResult>;

  constructor(props: Props) {
    super(props);

    const booking = props.interventionBooking;

    let testIntervention = undefined;
    const intervention = props.getInterventionById(booking.interventionId);
    if (intervention) {
      testIntervention = InterventionExternalCode[intervention.externalCode];
    }

    this.initialValues = {
      status: booking.status,
      testRegistrationDate: booking.testRegistrationDate,
      testRegistrationTime: booking.testRegistrationTime
    };

    const questionSets = props.questionSets;
    const questionSetMap = _.keyBy(questionSets, qs => qs.id);

    this.state = {
      requiredDataLoaded: false,
      questionSets,
      questionSetMap,
      currentQuestion: 0,
      testIntervention: testIntervention,
      modifySampleDateTime: false,
      testResultInProgress: false
    };

    this.fetchRequiredData(props);
  }

  fetchRequiredData = async (props: Props) => {

    const {questionSetMap} = this.state;

    const bookingId = props.interventionBooking.bookingId;

    const questionAnswersData = await getInterventionBookingAnswersData(bookingId, this.cancelTokenSource);
    if (questionAnswersData) {

      const questionSetId = questionAnswersData.questionSetId;
      const selectedQuestionSet = questionSetMap[questionSetId];

      let qaPairs: QuestionAnswerDto[] = selectedQuestionSet.questions.map(question => ({
        questionId: question.id,
        questionText: question.text,
        questionTextEn: question.textEn,
        questionTextFr: question.textFr,
        questionTextIt: question.textIt,
        selectedAnswers: [],
        remarks: ""
      }));

      if (questionAnswersData.questionAnswerPairs && questionAnswersData.questionAnswerPairs.length) {
        qaPairs = questionAnswersData.questionAnswerPairs;
      }

      this.initialValues = {
        ...this.initialValues,
        questionSetId,
        questionAnswerPairs: qaPairs
      };

      this.setState({
        selectedQuestionSet
      })
    }

    this.setState({
      requiredDataLoaded: true
    })
  }

  setTestResult = async (
      interventionBooking: InterventionTestBookingSearchResult,
      values: Partial<InterventionQuestionTestBookingSearchResult>) => {

    const {getMessage} = this.props;
    const {
      testRegistrationDate,
      testRegistrationTime,
      questionSetId,
      questionAnswerPairs
    } = values;

    this.setState({
      testResultInProgress: true,
      unansweredQuestions: undefined
    });

    const registrationTimestamp = this.state.modifySampleDateTime
        ? `${testRegistrationDate}T${testRegistrationTime}`
        : undefined;

    const request: Partial<QuestionTestResultRequest> = {
      treaterId: this.props.options.currentTreater.id,
      questionSetId,
      registrationTimestamp,
      questionAnswerPairs: questionAnswerPairs
    }

    const questionAnswersLength = questionAnswerPairs ? questionAnswerPairs.length : 0;
    const answeredQuestions = this.getNumberOfAnsweredQuestions(questionAnswerPairs);

    try {
      if (answeredQuestions == questionAnswersLength) {
        await setQuestionTestResult(interventionBooking.bookingId, request, this.cancelTokenSource);
        this.closeAndUpdate();
      } else {
        this.setState({
          unansweredQuestions: getMessage("anamnesis.unansweredQuestions.message")
        })
      }

      this.setState({
        testResultInProgress: false
      });

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }
  };

  verifyTestResult = async (
      interventionBooking: InterventionTestBookingSearchResult,
      values: Partial<InterventionQuestionTestBookingSearchResult>) => {

    const {getMessage} = this.props;

    const {
      testRegistrationDate,
      testRegistrationTime,
      questionSetId,
      questionAnswerPairs
    } = values;

    this.setState({
      testResultInProgress: true
    });

    const registrationTimestamp = this.state.modifySampleDateTime
        ? `${testRegistrationDate}T${testRegistrationTime}`
        : undefined;

    const request: Partial<QuestionTestResultRequest> = {
      treaterId: this.props.options.currentTreater.id,
      questionSetId,
      registrationTimestamp,
      questionAnswerPairs: questionAnswerPairs
    }

    const questionAnswersLength = questionAnswerPairs ? questionAnswerPairs.length : 0;
    const answeredQuestions = this.getNumberOfAnsweredQuestions(questionAnswerPairs);

    try {

      if (answeredQuestions == questionAnswersLength) {

        await verifyQuestionTestResult(interventionBooking.bookingId, request, this.cancelTokenSource);
        this.closeAndUpdate();

      } else {
        this.setState({
          unansweredQuestions: getMessage("anamnesis.unansweredQuestions.message")
        })
      }

      this.setState({
        testResultInProgress: false
      });

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }
  }

  setTestResultInProgress = (testResultInProgress: boolean) => {
    this.setState({
      testResultInProgress
    })
  }

  setControlledBy = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setControlledByBookingStatus(bookingId, treaterId, this.cancelTokenSource);
      if (response.certificateError) {
        this.setState({
          testResultSaveWarning:
              "patientCentral.overview.testResult.warn.officialCertificateNotCreated"
        });

        return;
      }
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  setResultRejected = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setResultRejectedBookingStatus(bookingId, treaterId, this.cancelTokenSource);
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors = [
      errorUtils.testBookingWithCanceledStatus
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  closeAndUpdate = () => {
    this.props.onCloseAndUpdate(this.props.interventionBooking.bookingId);
  };

  previousQuestion = (values: Partial<InterventionQuestionTestBookingSearchResult>) => {
    const {currentQuestion} = this.state;

    this.setUnansweredMessage(values);

    this.setState({
      currentQuestion: Math.max(currentQuestion - 1, 0)
    })
  }

  nextQuestion = (values: Partial<InterventionQuestionTestBookingSearchResult>) => {
    const {selectedQuestionSet, currentQuestion} = this.state;

    this.setUnansweredMessage(values);

    this.setState({
      currentQuestion: Math.min(currentQuestion + 1, selectedQuestionSet!.questions.length - 1)
    })
  }

  setQuestionValue = (fieldPath: string, value: boolean, form: FormApi) => {
    form.change(fieldPath, [value]);
    // functionality is disabled due to the question remarks
    // this.nextQuestion();
  }

  setUnansweredMessage = (values: Partial<InterventionQuestionTestBookingSearchResult>) => {
    const {questionAnswerPairs} = values;
    const answeredQuestions = this.getNumberOfAnsweredQuestions(questionAnswerPairs);
    const questionAnswersLength = questionAnswerPairs ? questionAnswerPairs.length : 0;

    if (answeredQuestions === questionAnswersLength) {
      this.setState({
        unansweredQuestions: undefined
      })
    }
  }

  getNumberOfAnsweredQuestions = (questionAnswerPairs: QuestionAnswerDto[] | undefined) => {
    let answeredQuestions = 0;
    if (questionAnswerPairs) {
      questionAnswerPairs.forEach(answer => {
        answeredQuestions = answer.selectedAnswers.length > 0
            ? answeredQuestions + 1
            : answeredQuestions
      })
    }

    return answeredQuestions;
  }

  renderQuestions = (form: FormApi): JSX.Element => {
    const {getMessage} = this.props;
    const {currentQuestion, questionSetMap} = this.state;

    const values: InterventionQuestionTestBookingSearchResult =
        form.getState().values as InterventionQuestionTestBookingSearchResult;

    const questionSetId = values.questionSetId;
    if (!questionSetId) {
      return <></>;
    }

    const questionSet = questionSetMap[questionSetId];
    if (!questionSet) {
      return <></>;
    }

    const questionMap = _.keyBy(questionSet.questions, q => q.id);

    const currentQuestionIx = currentQuestion < questionSet.questions.length ? currentQuestion : 0;

    const qaPairs = values.questionAnswerPairs;

    return (
        <>
          <div className="label"/>
          <div className="question-num-label">
            {getMessage("anamnesis.form.questionNum")}
            <span className="no-break">({`${currentQuestionIx + 1}/${questionSet.questions.length}`})</span>
          </div>

          <div className="question-container">
            <Button
                className="button-back"
                type="button"
                icon size="mini"
                disabled={currentQuestionIx <= 0}
                onClick={() => this.previousQuestion(values)}
            >
              <Icon name="chevron left"/>
            </Button>

            <div className="question">
              {
                qaPairs.map((qaPair, index: number) => {
                  const question: QuestionDto = questionMap[qaPair.questionId];

                  if (!question) {
                    return <div key={`question-wrapper-unknown-q-${qaPair.questionId}`}/>
                  }

                  const wrapperClass = index !== currentQuestionIx ? "inactive" : "";
                  return <div key={`question-wrapper-${question!.id}`} className={`question-wrapper ${wrapperClass}`}>
                    {this.renderSingleQuestion(`questionAnswerPairs[${index}]`, index, question, form)}
                  </div>;
                })
              }
            </div>

            <Button className="button-next"
                    type="button"
                    icon
                    size="mini"
                    disabled={currentQuestionIx >= questionSet.questions.length - 1}
                    onClick={() => this.nextQuestion(values)}
            >
              <Icon name="chevron right"/>
            </Button>
          </div>
        </>
    );
  }

  renderSingleQuestion = (questionAnswerFieldPath: string, index: number, question: QuestionDto, form: FormApi) => {
    const textMap: { [lang: string]: string } =
        {
          "de": question.text,
          "en": question.textEn || question.text,
          "fr": question.textFr || question.text,
          "it": question.textIt || question.text
        };

    const fieldPathRemarks = question.withRemarks ? `${questionAnswerFieldPath}.remarks` : undefined;

    return (
        <Fragment key={`question-fragment-${index}`}>
          <Label className="question-text" key={`question_label_${question.questionKey}`}>
            {textMap[this.props.language]}
          </Label>
          <DataEntryValue className="answer-value" key={`question_value_${question.questionKey}`}>
            {this.renderAnswerByType(`${questionAnswerFieldPath}.selectedAnswers`, index, question, form, fieldPathRemarks)}
          </DataEntryValue>
        </Fragment>
    );
  }

  renderAnswerByType = (fieldPath: string, index: number, question: QuestionDto, form: FormApi, fieldPathRemarks?: string) => {
    const {getMessage} = this.props;
    const {questionType} = question;

    const {status} = form.getState().values;
    const statusResultedOrVerified = status === InterventionBookingStatus.RESULTED
        || status === InterventionBookingStatus.VERIFIED;

    const remarks = () => {
      if (fieldPathRemarks) {
        return <Field
            name={fieldPathRemarks}
            component={StyledTextArea}
            label={getMessage('insert.answer')}
            disabled={statusResultedOrVerified}
        />
      }
    }

    switch (questionType) {
      case 'MULTI_ANSWER':
        const answers = question.possibleAnswers.map((answer, index) => (
                <ListItem key={`${fieldPath}_${index}`}>
                  <Field
                      name={fieldPath}
                      component="input"
                      type="checkbox"
                      value={answer}
                      disabled={statusResultedOrVerified}
                      validate={required}
                  />
                  {' ' + getMessage(answer)}
                </ListItem>
            )
        )
        return (
            <>
              <List>
                {answers}
              </List>
              {remarks()}
            </>
        );
      case 'SINGLE_ANSWER':
        return (
            <>
              <Field
                  name={fieldPath}
                  component={RadioGroup}
                  parse={convertValueToList}
                  format={extractValueFromList}
                  radioDefinitions={
                    generateRadioButtonOptions(
                        question.possibleAnswers,
                        question.possibleAnswers,
                        fieldPath,
                        getMessage,
                        question.possibleAnswers
                    )
                  }
                  disabled={statusResultedOrVerified}
                  validate={required}
              />
              {remarks()}
            </>
        );
      case 'FREE_TEXT':
        return (
            <>
              <Field
                  name={fieldPath}
                  component={Input}
                  fluid
                  placeholder={getMessage('insert.answer')}
                  parse={convertValueToList}
                  format={extractValueFromList}
                  disabled={statusResultedOrVerified}
                  validate={required}
              />
              {remarks()}
            </>
        );
      case 'YES_NO':
      default:
        const qa = _.get(form.getState().values, fieldPath);
        return (
            <>
              <div>
                <Button
                    className="yesno-button"
                    type="button"
                    size="mini"
                    primary={this.answerEquals(qa, true)}
                    onClick={() => this.setQuestionValue(fieldPath, true, form)}
                    disabled={statusResultedOrVerified}
                >
                  {getMessage("anamnesis.answer.yes")}
                </Button>

                <Button
                    className="yesno-button"
                    type="button"
                    size="mini"
                    primary={this.answerEquals(qa, false)}
                    onClick={() => this.setQuestionValue(fieldPath, false, form)}
                    disabled={statusResultedOrVerified}
                >
                  {getMessage("anamnesis.answer.no")}
                </Button>
              </div>
              {remarks()}
            </>
        );
    }
  }

  answerEquals = (answers: string[], value: any): boolean => {
    return answers && answers.length === 1 && (answers[0] === value || answers[0] === "" + value);
  }

  render(): React.ReactNode {

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Loader active/>
      </div>;
    }

    return (
        <FinalForm
            onSubmit={noop}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true, errors: true, error: true}}
            render={this.renderForm}
        />
    );
  };

  renderForm = (
      {values, form, handleSubmit, submitting, errors}: FormRenderProps): React.ReactNode => {

    const {interventionBooking, getMessage} = this.props;
    const {questionSetMap, unansweredQuestions, errorMessage} = this.state;

    const {questionSetId, questionAnswerPairs, status} = values;

    const fullTestResult = questionAnswerPairs.length;

    const questionSet = questionSetMap[questionSetId];
    let questionSetString = "";
    if (questionSet) {
      const nameMap: { [lang: string]: string } = {
        "de": questionSet.name,
        "en": questionSet.nameEn || questionSet.name,
        "fr": questionSet.nameFr || questionSet.name,
        "it": questionSet.nameIt || questionSet.name
      };
      questionSetString = nameMap[this.props.language];
    }

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Dimmer active={true}>
          <Loader inverted inline="centered" size="medium">
            {getMessage("patientCentral.loader.message")}
          </Loader>
        </Dimmer>
      </div>;
    }

    const statusResulted = status === InterventionBookingStatus.RESULTED;
    const statusVerified = status === InterventionBookingStatus.VERIFIED;

    const statusResultedOrVerified = statusResulted || statusVerified;

    return <StyledForm onSubmit={handleSubmit}>
      <FormContainer>

        <div className="patient-data">
          {`${interventionBooking.patientFirstName} ${interventionBooking.patientLastName}, ${isoToDisplayDate(interventionBooking.patientBirthDate)}`}
        </div>

        {
            !this.state.testResultSaveWarning && !this.state.testResultSaveError &&
            <PraxFormGrid>
              {questionSetId &&
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <StyledDataEntry>
                        {this.renderQuestions(form)}
                      </StyledDataEntry>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <QuestionSetView
                          questionSetName={questionSetString}
                          questionAnswerPairs={questionAnswerPairs}
                      />
                    </Grid.Column>
                  </Grid.Row>
              }
              <Grid.Row>
                <Grid.Column width={8}>
                  <div className="field-label">
                    <span className="inline-span">{getMessage("patientCentral.testResult.sampleDate")}:</span>
                    {
                        !statusResultedOrVerified &&
                        <span className="inline-span">
                      <CheckboxSc
                          checked={this.state.modifySampleDateTime}
                          label={getMessage("patientCentral.testResult.modifySampleDate")}
                          onChange={() => {
                            this.setState({modifySampleDateTime: !this.state.modifySampleDateTime})
                          }}
                      />
                    </span>
                    }
                  </div>

                  <div className="multi-field-row">
                    <div className="date-input">
                      <Field
                          name="testRegistrationDate"
                          component={DatePicker}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>

                    <div className="time-input">
                      <Field
                          name="testRegistrationTime"
                          size="mini"
                          component={TimeInput}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
        }

        {
            this.state.testResultSaveWarning &&
            <div className="test-result-warning">
              {getMessage(this.state.testResultSaveWarning)}
            </div>
        }

        {
            this.state.errorMessage &&
            <StyledErrorMessage>{this.state.errorMessage}</StyledErrorMessage>
        }

        <FormButtonsRow>
          {
              (statusResulted && !statusVerified) && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    form.change("status", InterventionBookingStatus.AWAITING_COMPLETION);
                  }
                  }
                  disabled={this.state.testResultInProgress}
              >
                {getMessage("patientCentral.overview.changeTestResult.button")}
              </Button>
          }
          {
              statusResultedOrVerified && !interventionBooking.testResultVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    this.verifyTestResult(interventionBooking, values);
                  }}
                  disabled={this.state.testResultInProgress}
              >
                {getMessage("patientCentral.overview.verifyTestResult.button")}
              </Button>
          }
          {
              !statusResultedOrVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={form.getState().invalid || !fullTestResult || this.state.testResultInProgress}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setTestResult(interventionBooking, values);
                  }}
              >
                {getMessage("button.save")}
              </Button>
          }
          {
              statusVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!fullTestResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setControlledBy();
                  }}
              >
                {getMessage("button.confirm")}
              </Button>
          }
          {
              statusVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!fullTestResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setResultRejected();
                  }}
              >
                {getMessage("button.reject")}
              </Button>
          }
          <Button compact type="button"
                  disabled={submitting}
                  onClick={() => {

                    const {testResultSaveWarning} = this.state;

                    if (testResultSaveWarning) {
                      this.closeAndUpdate();
                    } else {
                      this.props.onClose();
                    }
                  }}
          >
            {getMessage("action.back")}
          </Button>
          {unansweredQuestions !== undefined &&
              <span className="unanswered-questions-error">
                {unansweredQuestions}
              </span>
          }
        </FormButtonsRow>
      </FormContainer>
    </StyledForm>;
  }

}

export default withMessages(withAuthContext(withPatientCentralRefDataContext(SetQuestionTestResultModal)));
